'use client'

import { authClient } from '@repo/auth/client'
import { OTP_LENGTH, OTP_TTL_MINUTES } from '@repo/auth/otp'
import Link from 'next/link'
import { useState } from 'react'

import {
  AUTH_ACCENT_GLOW,
  AUTH_ACCENT_GRADIENT,
  authInputClass,
} from '@/components/molecules/auth-card'
import { useToast } from '@/components/providers/ToastProvider'
import { authErrorMessage } from '@/lib/auth-errors'
import { BODY, MICRO, SUBHEADING } from '@/lib/typography'
import { cn } from '@/lib/utils'

/**
 * The second half of registering: proving the address is yours.
 *
 * A code rather than a link, and the account already EXISTS by the time this
 * shows — sign-up signed the person in. So this screen never traps anybody:
 * "zrobię to później" is a real option and the panel keeps asking. What it
 * gates is payment (spec §3), which is a decision the checkout makes, not this
 * form.
 *
 * The copy used to say "wysłaliśmy potwierdzenie na …" while nothing was sent
 * at all. It says it now because it is now true.
 */
export function RegisterCodeStep({ email, locale }: { email: string; locale: string }) {
  const { toast } = useToast()
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [resending, setResending] = useState(false)
  const [verified, setVerified] = useState(false)

  const ready = code.trim().length === OTP_LENGTH && !busy

  const verify = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!ready) return
    setBusy(true)
    const res = await authClient.emailOtp
      .verifyEmail({ email, otp: code.trim() })
      // A dead connection is a refusal too, and takes the call site's sentence.
      .catch(() => ({ error: {} }))
    setBusy(false)

    if ('error' in res && res.error) {
      toast({
        kind: 'error',
        title: 'Kod nie pasuje',
        // Three attempts and the code dies, so the sentence has to say what to
        // do next rather than just that something went wrong.
        description: authErrorMessage(res.error, 'Sprawdź go jeszcze raz albo poproś o nowy.'),
      })
      setCode('')
      return
    }
    setVerified(true)
    toast({ kind: 'success', title: 'Adres potwierdzony', description: 'Konto jest gotowe.' })
  }

  const resend = async () => {
    setResending(true)
    const res = await authClient.emailOtp
      .sendVerificationOtp({ email, type: 'email-verification' })
      // A dead connection is a refusal too, and takes the call site's sentence.
      .catch(() => ({ error: {} }))
    setResending(false)
    toast(
      'error' in res && res.error
        ? { kind: 'error', title: 'Nie udało się wysłać kodu', description: 'Spróbuj za chwilę.' }
        : { kind: 'success', title: 'Nowy kod w drodze', description: `Wysłany na ${email}.` },
    )
    setCode('')
  }

  if (verified) {
    return (
      <div className="flex flex-col items-center text-center">
        <Badge />
        <h2 className={cn(SUBHEADING, 'text-white')}>Konto gotowe</h2>
        <p className={cn(BODY, 'mt-2 text-white/65')}>
          Adres <span className="text-white">{email}</span> jest potwierdzony.
        </p>
        <Link
          href={`/${locale}/panel`}
          className={cn(
            BODY,
            'mt-6 inline-flex items-center gap-2 rounded-full px-5 py-3 font-semibold text-white',
          )}
          style={{ background: AUTH_ACCENT_GRADIENT, boxShadow: AUTH_ACCENT_GLOW }}
        >
          Otwórz panel
        </Link>
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      <h2 className={cn(SUBHEADING, 'text-center text-white')}>Sprawdź skrzynkę</h2>
      <p className={cn(BODY, 'mt-2 text-center text-white/65')}>
        Wysłaliśmy {OTP_LENGTH}-cyfrowy kod na <span className="text-white">{email}</span>. Jest
        ważny {OTP_TTL_MINUTES} min.
      </p>

      <form className="mt-6 flex flex-col gap-4" onSubmit={verify}>
        <input
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH))}
          // `one-time-code` is what lets a phone offer the code from the
          // notification instead of making somebody switch apps to read it.
          autoComplete="one-time-code"
          inputMode="numeric"
          aria-label="Kod z wiadomości"
          placeholder="000000"
          className={cn(
            authInputClass,
            'flou-auth-field',
            'py-3.5 text-center font-mono text-[1.6rem] tracking-[0.4em]',
          )}
        />
        <button
          type="submit"
          disabled={!ready}
          className={cn(
            BODY,
            'w-full rounded-xl py-3.5 font-semibold text-white transition-all duration-300',
            ready ? 'hover:brightness-110 active:scale-[0.99]' : 'cursor-not-allowed opacity-50',
          )}
          style={{ background: AUTH_ACCENT_GRADIENT, boxShadow: AUTH_ACCENT_GLOW }}
        >
          {busy ? 'Sprawdzam…' : 'Potwierdź adres'}
        </button>
      </form>

      <div className={cn(MICRO, 'mt-5 flex flex-col items-center gap-2 text-white/55')}>
        <button
          type="button"
          onClick={resend}
          disabled={resending}
          className="font-semibold text-white/70 underline-offset-4 transition-colors hover:text-white disabled:opacity-50"
        >
          {resending ? 'Wysyłam…' : 'Wyślij kod jeszcze raz'}
        </button>
        {/* Never a dead end: the account exists and the session is live, so
            leaving now costs nothing but a nudge in the panel. */}
        <Link href={`/${locale}/panel`} className="transition-colors hover:text-white/70">
          Zrobię to później
        </Link>
      </div>
    </div>
  )
}

function Badge() {
  return (
    <div
      aria-hidden
      className="mb-4 flex h-12 w-12 items-center justify-center rounded-full text-white"
      style={{ background: AUTH_ACCENT_GRADIENT, boxShadow: AUTH_ACCENT_GLOW }}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <title>Gotowe</title>
        <path
          d="M5 12.5l4.5 4.5L19 7.5"
          stroke="currentColor"
          strokeWidth="2.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  )
}
